import { AfterViewInit, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { 
  BulkUpdateReleaseItemsRequest, BulkUpdateReleaseItemsResponse, FileParameter, GetReleaseRequest, GetReleaseResponse, ListReleaseIssuesRequest, 
  ListReleaseIssuesResponse, ListRenderedOutputsRequest, ListRenderedOutputsResponse, ProductName, Release, ReleaseIssue, 
  ReleaseIssuePriority, ReleaseIssueStatus, ReleaseItem, ReleaseItemFieldToUpdate, ReleaseStatus, RenderedOutput, RenderReleaseStatus, SetReleaseItemViewedRequest, SetReleaseItemViewedResponse, SituViewWebApiClient, 
  UpdateReleaseRequest, UpdateReleaseResponse
} from 'situview-webapi-client';
import { AuthService, BreadcrumbService, CompanyService, ConfigService, ConfirmDialogService, ProductNameService, ReleaseService, StateService, Utils } from 'src/app/services';
import { SituBaseComponent } from 'src/app/shared/situ-base-component';
import { ViewControls } from 'src/app/shared/models/view-controls';
import { PageEvent } from '@angular/material/paginator';
import { ConfirmDialogData } from 'src/app/components/confirm-dialog/ConfirmDialogData';
import { GetMostPopularRenderedHomesByBuilderRequest, GetMostPopularRenderedHomesByBuilderResponse, SituAnalyticsWebApiClient, ViewMostPopularHomeLogItem } from 'situanalytics-webapi-client';
import { RenameDialogComponent } from 'src/app/components/rename-dialog/rename-dialog.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { interval, of } from 'rxjs';
import { concatMap, takeWhile } from 'rxjs/operators';
import { ReleaseStyleTemplateDialogComponent, ReleaseStyleTemplateDialogComponentResultData } from '../release-style-template-dialog/release-style-template-dialog.component';
import { DownloadSituFacadesRequest, SituRenderServiceWebApiClient } from 'siturenderservice-webapi-client';
import * as FileSaver from 'file-saver';
import { ReleaseIssueDialogComponent, ReleaseIssueDialogComponentResultData } from '../release-issue-dialog/release-issue-dialog.component';

@Component({
  selector: 'release-edit',
  templateUrl: './release-edit.component.html',
  styleUrls: ['./release-edit.component.scss']
})

export class ReleaseEditComponent extends SituBaseComponent implements AfterViewInit {
  viewControls = {
    pageIndex: 0,
    pageSize: 100,
    totalItems: 0,
    pageSizeOptions: [25, 50, 100]
  } as ViewControls;

  id: string = '';
  released: boolean = false;
  top100: boolean = false;
  data: Release | undefined;
  thumbnailData: string | undefined;
  releaseStatuses = ReleaseStatus;
  filteredReleaseItems: ReleaseItem[] = [];
  selectedReleaseItems: string[] = [];
  productNames = ProductName;
  renderReleaseStatuses = RenderReleaseStatus;
  logItems: ViewMostPopularHomeLogItem[] | undefined;

  /* Filters */
  searchTerm: string | undefined;
  selectedHome: string | undefined;
  selectedMasterFile: string | undefined;
  selectedOption: string | undefined;
  selectedStyleTemplate: string | undefined;
  selectedReleaseStatus: ReleaseStatus | undefined;
  selectedProductName: ProductName | undefined;
  commentsFilter: boolean = false;
  ticketFilter: boolean = false;
  standardFilter: boolean = false;
  hiResFilter: boolean = false;
  issueFilter: string | undefined;
  viewedFilter: boolean = false;
  /* Filters */

  updateReleaseItemValue: string | undefined;
  updateReleaseItemEnumValue: number | undefined;
  releaseItemFieldToUpdateOptions = ReleaseItemFieldToUpdate;
  releaseItemFieldToUpdate: ReleaseItemFieldToUpdate | undefined;

  situLinkBaseUrl: string | undefined;
  situPortalBaseUrl: string | undefined;

  /* Query Parameters */
  readonly SearchParamName: string = "search";
  readonly HomeParamName: string = "home";
  readonly MasterFileParamName: string = "masterfile";
  readonly OptionsParamName: string = "options";
  readonly StyleTemplateParamName: string = "template";
  readonly RenderTypeParamName: string = "type";
  readonly StatusParamName: string = "status";
  readonly CommentsParamName: string = "comments";
  readonly TicketsParamName: string = "tickets";
  readonly StandardParamName: string = "standard";
  readonly HiResParamName: string = "hires";
  readonly IssueParamName: string = "issue";
  readonly ViewedParamName: string = "viewed";
  /* Query Parameters */

  private _loading = false;
  public get loading(): boolean { return this._loading; }

  public set loading(val: boolean) {
    this._loading = val;
    this.breadcrumbService.setIsLoading(val);
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private route: ActivatedRoute,
    private router: Router,
    private snackBar: MatSnackBar,
    private breadcrumbService: BreadcrumbService,
    private situViewApiClient: SituViewWebApiClient,
    private releaseService: ReleaseService,
    private dialogService: ConfirmDialogService,
    private analyticsClient: SituAnalyticsWebApiClient,
    private dialog: MatDialog,
    private configService: ConfigService,
    private productNameService: ProductNameService,
    private renderServiceClient: SituRenderServiceWebApiClient,
    private companyService: CompanyService) {
    super(authService, stateService);
    this.id = this.route.snapshot.params['id'];
    if (this.id.toLowerCase() === "complete") this.released = true;
    if (this.id.toLowerCase() === "top100") {
      this.released = true;
      this.top100 = true;
    }
    this.situLinkBaseUrl = this.configService.config.situLinkBaseUrl;
    this.situPortalBaseUrl = this.configService.config.situPortalBaseUrl;

    if (this.route.snapshot.queryParams[this.SearchParamName]) {
      this.searchTerm = this.route.snapshot.queryParams[this.SearchParamName];
    }

    if (this.route.snapshot.queryParams[this.HomeParamName]) {
      this.selectedHome = this.route.snapshot.queryParams[this.HomeParamName];
    }

    if (this.route.snapshot.queryParams[this.MasterFileParamName]) {
      this.selectedMasterFile = this.route.snapshot.queryParams[this.MasterFileParamName];
    }

    if (this.route.snapshot.queryParams[this.OptionsParamName]) {
      this.selectedOption = this.route.snapshot.queryParams[this.OptionsParamName];
    }

    if (this.route.snapshot.queryParams[this.StyleTemplateParamName]) {
      this.selectedStyleTemplate = this.route.snapshot.queryParams[this.StyleTemplateParamName];
    }

    if (this.route.snapshot.queryParams[this.RenderTypeParamName]) {
      this.selectedProductName = Number(this.route.snapshot.queryParams[this.RenderTypeParamName]) as ProductName;
    }

    if (this.route.snapshot.queryParams[this.StatusParamName]) {
      this.selectedReleaseStatus = Number(this.route.snapshot.queryParams[this.StatusParamName]) as ReleaseStatus;
    }

    if (this.route.snapshot.queryParams[this.CommentsParamName]) {
      this.commentsFilter = this.route.snapshot.queryParams[this.CommentsParamName] === 'true';
    }

    if (this.route.snapshot.queryParams[this.TicketsParamName]) {
      this.ticketFilter = this.route.snapshot.queryParams[this.TicketsParamName] === 'true';
    }

    if (this.route.snapshot.queryParams[this.StandardParamName]) {
      this.standardFilter = this.route.snapshot.queryParams[this.StandardParamName] === 'true';
    }

    if (this.route.snapshot.queryParams[this.HiResParamName]) {
      this.hiResFilter = this.route.snapshot.queryParams[this.HiResParamName] === 'true';
    }
    
    if (this.route.snapshot.queryParams[this.ViewedParamName]) {
      this.viewedFilter = this.route.snapshot.queryParams[this.ViewedParamName] === 'true';
    }

    if (this.route.snapshot.queryParams[this.IssueParamName]) {
      this.issueFilter = this.route.snapshot.queryParams[this.IssueParamName];
      this.showReleaseIssues = true;
    }
  }

  protected onSelectedCompanyChange(): void {
    this.load();
    if (this.released) this.loadReleases();
  }

  ngAfterViewInit(): void {
    this.setupIntersectionObserver();
  }

  /* auto paging */
  pageSize = 8;
  pageLength = 8;
  @ViewChild('showMoreProductsMarker') showMoreProductsMarker: ElementRef | undefined;
  private observer: IntersectionObserver | undefined;
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && this.hasMoreProductsToShow()) {
          this.showMoreProducts();
        }
      });
    });

    if (this.showMoreProductsMarker?.nativeElement) {
      this.observer.observe(this.showMoreProductsMarker.nativeElement);
    }
  }

  showMoreProducts() {
    const incrementSize = 2;
    const intervalTime = 200;
    const targetPageLength = this.pageLength + this.pageSize;
    this._subscriptions.push(interval(intervalTime).pipe(
      takeWhile(() => this.pageLength < targetPageLength && this.pageLength < this.filteredReleaseItems.length),
      concatMap(() => {
        const nextPageLength = Math.min(this.pageLength + incrementSize, targetPageLength, this.filteredReleaseItems.length);
        return of(nextPageLength);
      })
    ).subscribe((nextPageLength) => {
      this.pageLength = nextPageLength;
    }));
  }

  hasMoreProductsToShow(): boolean {
    return this.filteredReleaseItems.length > this.pageLength;
  }
  /* auto paging */

  approveForLive(releaseItem: ReleaseItem) {
    if (!this.selectedCompany?.id || !releaseItem?.id || releaseItem.releaseStatus === ReleaseStatus.Live) {
      return;
    }

    this.dialogService.open({
      title: 'Approve for Live?',
      message: 'Do you approve this release item for Live?',
      cancelText: 'No',
      confirmText: 'Yes',
      actionIcon: 'check',
      actionColour: 'accent'
    } as ConfirmDialogData);
    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.releaseService.SetStatusReleaseItems(this.selectedCompany!.id!, [releaseItem.id!], ReleaseStatus.ApprovedForLive)
            .then((response: BulkUpdateReleaseItemsResponse) => {
              if (response.success) {
                this.load();
                this.clearValues();
              } else {
                this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
              }
            })
            .catch((err: any) => {
              this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
            });
        } else {
          this.loading = false;
        }
      }));
  }

  isExpired(releaseDate: Date) {
    return releaseDate < new Date();
  }

  getStatusColor(status: ReleaseStatus) {
    return this.releaseService.getStatusColor(status, this.isDarkMode);
  }

  getProductColour(productName: ProductName) {
    return this.productNameService.getProductNameConfiguration(productName as any)?.productColor;
  }

  getLivePercentage() {
    if (this.data?.releaseItems) {
      let totalItems = this.data.releaseItems.length;
      if (totalItems === 0) {
        return 0;
      }
      let liveItems = this.data.releaseItems.filter(x => x.releaseStatus === ReleaseStatus.Live).length;
      return Math.round((liveItems / totalItems) * 100);
    } else {
      return 0;
    }
  }

  edit(field: string) {
    if (!field) return;

    const dialogRef = this.dialog.open(RenameDialogComponent, {
        width: '350px',
        height: '60%',
        data: { 
            existingName: field === 'name' ? this.data?.name : this.data?.description
        }
    });

    this._subscriptions.push(dialogRef.afterClosed().subscribe(name => {
        if (name !== undefined) {            
          let request = new UpdateReleaseRequest();
            request.companyId = this.selectedCompany?.id;
            request.releaseId = this.data?.id;
            if (field === 'name') {
              request.name = name;
              request.description = this.data?.description;
            } else {
              request.name = this.data?.name;
              request.description = name;
            }
            request.releaseDate = this.data?.releaseDate;

            this.situViewApiClient.updateRelease(request)
                .then((response: UpdateReleaseResponse) => {
                    if (response.success) {
                        this.load();
                        this.snackBar.open(`Updated release!`, 'OK', { duration: 2000 });
                    } else {
                        this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
                    }
                })
                .catch((err: any) => {
                    this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
                });
        }
    }));
  }

  addDays() {
    if (this.data) {
      let request = new UpdateReleaseRequest();
      request.companyId = this.selectedCompany?.id;
      request.releaseId = this.data?.id;
      request.name = this.data?.name;
      request.description = this.data?.description;
      request.renderReleaseStatus = this.data?.renderReleaseStatus;
      const currentDate = this.data?.releaseDate ? this.data.releaseDate : new Date();
      const newDate = new Date(currentDate);
      newDate.setDate(newDate.getDate() + 10);
      request.releaseDate = newDate;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated release!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }

  togglePreRelease() {
    if (this.data) {
      let request = new UpdateReleaseRequest();
      request.companyId = this.selectedCompany?.id;
      request.releaseId = this.data?.id;
      request.name = this.data?.name;
      request.description = this.data?.description;
      request.renderReleaseStatus = this.data?.renderReleaseStatus === RenderReleaseStatus.PreRelease ? RenderReleaseStatus.InFlight : RenderReleaseStatus.PreRelease;
      request.releaseDate = this.data?.releaseDate;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated release!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }  
  toggleRaytraced() {
    if (this.data) {
      let request = new UpdateReleaseRequest();
      request.companyId = this.selectedCompany?.id;
      request.releaseId = this.data?.id;
      request.name = this.data?.name;
      request.description = this.data?.description;
      request.renderReleaseStatus = this.data?.renderReleaseStatus;
      request.releaseDate = this.data?.releaseDate;
      request.situRaytracedPlus = !this.data?.situRaytracedPlus;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated raytraced setting!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }
  toggleSituPlan() {
    if (this.data) {
      let request = new UpdateReleaseRequest();
      request.companyId = this.selectedCompany?.id;
      request.releaseId = this.data?.id;
      request.name = this.data?.name;
      request.description = this.data?.description;
      request.renderReleaseStatus = this.data?.renderReleaseStatus;
      request.releaseDate = this.data?.releaseDate;
      request.situPlan = !this.data?.situPlan;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated situPLAN setting!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }
  toggleSituRender() {
    if (this.data) {
      let request = new UpdateReleaseRequest();
      request.companyId = this.selectedCompany?.id;
      request.releaseId = this.data?.id;
      request.name = this.data?.name;
      request.description = this.data?.description;
      request.renderReleaseStatus = this.data?.renderReleaseStatus;
      request.releaseDate = this.data?.releaseDate;
      request.situRender = !this.data?.situRender;

      this.situViewApiClient.updateRelease(request)
          .then((response: UpdateReleaseResponse) => {
              if (response.success) {
                  this.load();
                  this.snackBar.open(`Updated situRENDER setting!`, 'OK', { duration: 2000 });
              } else {
                  this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
              }
          })
          .catch((err: any) => {
              this.snackBar.open(`Error updating release!`, 'OK', { duration: 2000 });
          });
    }
  }

  getViews(artifactId: string) {
    return this.logItems?.find(x => x.artifactId === artifactId)?.numberOfViews;
  }

  openLinkInNewWindow(event: MouseEvent, route: string, queryParams: { [key: string]: any }) {
    event.stopPropagation();
    const url = this.router.createUrlTree([route], { queryParams }).toString();
    window.open(url, '_blank');
  }

  openSituLinkInNewWindow(event: MouseEvent, artifactId: string, isFacade: boolean = false, showHistory: boolean = false) {
    event.stopPropagation();
    const url = this.getSituLinkUrl(artifactId, isFacade, showHistory);
    window.open(url, '_blank');
  }

  /* releases */
  releases: Release[] | undefined;
  loadReleases() {
    this.loading = true;

    this.releaseService.loadReleases(this.selectedCompany?.id!)
      .then((releases: Release[] | undefined) => {
        this.releases = releases ?? [];
      })
      .finally(() => this.loading = false);
  }

  deleteConfirm() {
    this.releaseItemFieldToUpdate = ReleaseItemFieldToUpdate.Delete;
    this.dialogService.open({
      title: `Would you like to delete the selected release items?`,
      message: 'Delete the selected release Items',
      cancelText: 'Cancel',
      confirmText: 'Delete',
      actionIcon: 'priority_high',
      actionColour: 'warn'
    });
    this._subscriptions.push(this.dialogService.confirmed().subscribe(confirmed => {
      if (confirmed) {
        this.setValues();
      }
    }));
  }

  onPageChange(event: PageEvent) {
    this.viewControls.pageSize = event.pageSize;
    this.viewControls.pageIndex = event.pageIndex;

    this.load();
  }

  setValues() {
    if (this?.data?.situRaytracedPlus && this.releaseItemFieldToUpdate === ReleaseItemFieldToUpdate.ReleaseStatus && this.updateReleaseItemEnumValue == ReleaseStatus.StandardReviewed) {
      this.dialogService.open({
        title: 'Queue RayTracedPlus renders?',
        message: 'Have the homes been reviewed in standard res first? Excessive high quality renders may cause delays and attract additional cost.',
        cancelText: 'No',
        confirmText: 'Yes',
        actionIcon: 'check',
        actionColour: 'accent'
      } as ConfirmDialogData);
      this._subscriptions.push(this.dialogService.confirmed()
        .subscribe(confirmed => {
          if (confirmed) {
            this.bulkSetReleaseItemProperties();
          } else {
            this.loading = false;
          }
        }));
    } else {
      this.bulkSetReleaseItemProperties();
    }
  }

  clearValues() {
    this.updateReleaseItemValue = undefined;
    this.updateReleaseItemEnumValue = undefined;
  }
  
  clearSelection() {
    this.selectedReleaseItems = [];
    this.releaseItemFieldToUpdate = undefined;
  }

  dialogRef: MatDialogRef<ReleaseStyleTemplateDialogComponent> | undefined;
  copyToStyleTemplate() {
    const homeConfigIds = this.filteredReleaseItems
      .filter(item => this.selectedReleaseItems.includes(item.id!))
      .map(x => x.homeConfigId); 

    if (!homeConfigIds || homeConfigIds.length < 1) {
      this.showMessage("Please select some home configurations");
      return;
    }

    this.dialogRef = this.dialog.open(ReleaseStyleTemplateDialogComponent, {
      panelClass: 'modal-panel',
      width: '60%',
      height: '80%',
      data: {
        companyId: this.selectedCompany?.id,
        productName: this.selectedProductName,
        homeConfigIds: homeConfigIds
      }
    });

    this._subscriptions.push(this.dialogRef.afterClosed()
      .subscribe((data: ReleaseStyleTemplateDialogComponentResultData) => {
        if (data?.releaseCount && data?.releaseItemCount) {
          this.showMessage(`${data.releaseItemCount} release items added across ${data.releaseCount} releases!`);
          this.load();
        }
      })
    );
  }

  selectAll() {
    this.selectedReleaseItems = this.filteredReleaseItems
      ?.map(x => x.id)
      .filter((id): id is string => id !== undefined) ?? [];
  }

  isSelected(releaseItemId: string): boolean {
    return this.selectedReleaseItems.findIndex(x => x === releaseItemId) !== -1;
  }

  toggleSelection(releaseItemId: string) {
    const index = this.selectedReleaseItems.findIndex(x => x === releaseItemId);

    if (index === -1) {
      this.selectedReleaseItems.push(releaseItemId);
    } else {
      this.selectedReleaseItems.splice(index, 1);
    }
  }

  filterHome(home: string) {
    if (home && this.selectedHome !== home) {
      this.selectedHome = home;
    } else if (!home) {
      this.selectedHome = undefined;
    }
    this.filterData();
  }

  filterMasterFile(masterFile: string) {
    if (masterFile && this.selectedMasterFile !== masterFile) {
      this.selectedMasterFile = masterFile;
    } else if (!masterFile) {
      this.selectedMasterFile = undefined;
    }
    this.filterData();
  }

  filterOption(option: string) {
    if (option && this.selectedOption !== option) {
      this.selectedOption = option;
    } else if (!option) {
      this.selectedOption = undefined;
    }
    this.filterData();
  }
  
  filterStyleTemplate(styleTemplate: string) {
    if (styleTemplate && this.selectedStyleTemplate !== styleTemplate) {
      this.selectedStyleTemplate = styleTemplate;
    } else if (!styleTemplate) {
      this.selectedStyleTemplate = undefined;
    }
    this.filterData();
  }

  filterReleaseStatus(releaseStatus: ReleaseStatus) {
    if (releaseStatus && this.selectedReleaseStatus !== releaseStatus) {
      this.selectedReleaseStatus = releaseStatus;
    } else if (!releaseStatus) {
      this.selectedReleaseStatus = undefined;
    }
    this.filterData();
  }

  filterProductName(productName: ProductName) {
    if (productName !== undefined && this.selectedProductName !== productName) {
      this.selectedProductName = productName;
    } else if (productName === undefined) {
      this.selectedProductName = undefined;
    }
    this.filterData();
  }

  filterComments(shouldFilter: boolean) {
    this.commentsFilter = shouldFilter;
    this.filterData();
  }

  filterTickets(shouldFilter: boolean) {
    this.ticketFilter = shouldFilter;
    this.filterData();
  }

  filterStandard(shouldFilter: boolean) {
    this.standardFilter = shouldFilter;
    this.filterData();
  }

  filterHiRes(shouldFilter: boolean) {
    this.hiResFilter = shouldFilter;
    this.filterData();
  }

  filterIssue(issue: string) {
    if (issue && this.issueFilter !== issue) {
      this.issueFilter = issue;
    } else if (!issue) {
      this.issueFilter = undefined;
    }
    this.filterData();
  }

  filterViewed(shouldFilter: boolean) {
    this.viewedFilter = shouldFilter;
    this.filterData();
  }

  downloadSelectedFacades(hiRes: boolean = false) {
    let artifactIds;
    if (hiRes) {
      artifactIds = this.filteredReleaseItems
      .filter(item => this.selectedReleaseItems.includes(item.id!))
      .map(x => x.higherResVersion ?? "")
      .filter(x => x);
  
      if (!artifactIds || artifactIds.length < 1) {
        this.showMessage("Please select some rendered HiRes release items");
        return;
      }
    } else {
      artifactIds = this.filteredReleaseItems
      .filter(item => this.selectedReleaseItems.includes(item.id!))
      .map(x => x.latestVersion ?? "")
      .filter(x => x);
  
      if (!artifactIds || artifactIds.length < 1) {
        this.showMessage("Please select some rendered release items");
        return;
      }
    }

    let request = new DownloadSituFacadesRequest();
    request.companyId = this.selectedCompany?.id;
    request.facadeIds = artifactIds;

    this.renderServiceClient.downloadSituFacades(request) 
      .then((response: any) => {
          FileSaver.saveAs(response.data, response.fileName);
      })
      .catch((err: any) => this.showMessage("There was an error downloading the file!"));
  }

  downloadCSV() {
    if (!this.filteredReleaseItems) {
      return;
    }

    const csvData = this.convertToCSV(this.filteredReleaseItems);
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const timestamp = Utils.getCurrentTimestamp();
    const fileName = `release_items_${timestamp}.csv`;

    // For IE 11
    if ((navigator as any).msSaveBlob) {
      (navigator as any).msSaveBlob(blob, fileName);
    } else {
      // Modern browsers
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  private convertToCSV(releaseItems: ReleaseItem[]): string {
    let str = '';
    let row = '';

    const headers = ['Home', 'Link', 'HiRes Link', 'Product Name', 'Status', 'Notes'];
    row = headers.join(',') + '\r\n';
    str += row;

    releaseItems.forEach((item: ReleaseItem) => {
      let line = '';
      line += `${item.home} / ${item.masterFile} / ${item.options?.split(",").join("~")} / ${item.styleTemplate},`;
      line += `${this.getSituLinkUrl(item.latestVersion!, item.productName === ProductName.Facade)},`;
      line += `${this.getSituLinkUrl(item.higherResVersion!, item.productName === ProductName.Facade)},`;
      line += `${this.productNames[item.productName!]},`;
      line += `${this.releaseStatuses[item.releaseStatus!]},`;
      line += `${item.comments ? item.comments : ""},`;
      str += line + '\r\n';
    });

    return str;
  }

  getSituLinkUrl(artifactId: string, isFacade: boolean = false, showHistory: boolean = false): string {
    if (!artifactId) {
      return "";
    }

    const baseUrl = this.situLinkBaseUrl 
        ? `https://${isFacade ? 'facade' : 'view'}.${this.situLinkBaseUrl}`
        : this.situPortalBaseUrl!;
    
    const url = new URL(baseUrl);
    url.pathname = artifactId;
    if (showHistory) url.searchParams.append("history", "true")

    return url.toString();
  }

  uploadCSV() {
    if (!this.data?.name || !this.selectedCompany?.code) {
      this.showMessage("Release data not loaded. Please wait and try again.");
      return;
    }

    // Show confirmation dialog before proceeding
    this.dialogService.open({
      title: 'Confirm Release Items Import',
      message: `Are you sure you want to import release items into ${this.selectedCompany.code}'s "${this.data.name}" release?`,
      cancelText: 'Cancel',
      confirmText: 'Import Release Items',
      actionIcon: 'file_upload',
      actionColour: 'primary'
    } as ConfirmDialogData);

    this._subscriptions.push(this.dialogService.confirmed()
      .subscribe(confirmed => {
        if (confirmed) {
          this.proceedWithCSVUpload();
        }
      }));
  }

  private proceedWithCSVUpload() {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = '.csv';
    fileInput.style.display = 'none';
    
    fileInput.onchange = (event: any) => {
      const file = event.target.files[0];
      if (!file) return;
      
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if (fileExt !== 'csv') {
        this.showMessage("Only CSV files are allowed");
        return;
      }
      
      this.loading = true;
      
      // Create FileParameter object for the API
      const fileParameter = {
        data: file,
        fileName: file.name
      };
      
      this.situViewApiClient.importReleaseItemsFromCsv(fileParameter, this.selectedCompany?.id, this.id)
        .then((response: any) => {
          this.loading = false;
          let message = `Import completed! `;
          message += `Processed: ${response.totalProcessed || 0}, `;
          message += `Imported: ${response.totalImported || 0}, `;
          message += `Skipped: ${response.totalSkipped || 0}`;
          
          if (response.accessoryBundlesCreated > 0) {
            message += `, Accessory Bundles: ${response.accessoryBundlesCreated}`;
          }
          
          this.showMessage(message);
          this.load();
        })
        .catch((error: any) => {
          this.loading = false;
          this.showMessage("Error importing CSV file!");
        });
    };
    
    document.body.appendChild(fileInput);
    fileInput.click();
    document.body.removeChild(fileInput);
  }


  downloadJSON() {
    const jsonData = JSON.stringify(this.filteredReleaseItems?.map(x => {
      return {
        "homeConfig": {
          "home": x.home,
          "masterFile": x.masterFile,
          "options": x.options ? x.options?.split(',') : [],
          "themes": []
        },
        "styleChoicesConfigName": x.styleTemplate,
        "productName": this.productNames[x.productName!],
        "releaseStatus": this.releaseStatuses[x.releaseStatus!]
      };
    }));
    const blob = new Blob([jsonData], { type: 'application/json;charset=utf-8;' });
    const timestamp = Utils.getCurrentTimestamp();
    const fileName = `release_items_${timestamp}.json`;

    // For IE 11
    if ((navigator as any).msSaveBlob) {
      (navigator as any).msSaveBlob(blob, fileName);
    } else {
      // Modern browsers
      const link = document.createElement('a');
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', fileName);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    }
  }

  releaseStats: {
    uniqueHomeConfigs: number;
    uniqueHomes: number;
    uniqueMasterFiles: number;
    uniqueStyleTemplates: number;
    uniqueOptions: number;
    totalItems: number;
    totalRenderTimeRemaining: number;
  } = {
    uniqueHomeConfigs: 0,
    uniqueHomes: 0,
    uniqueMasterFiles: 0,
    uniqueStyleTemplates: 0,
    uniqueOptions: 0,
    totalItems: 0,
    totalRenderTimeRemaining: 0
  };
  
  // Call this method from filterData() after updating filteredReleaseItems
  calculateReleaseStats() {
    // Initialize empty Sets to track unique values
    const uniqueHomeConfigs = new Set<string>();
    const uniqueHomes = new Set<string>();
    const uniqueMasterFiles = new Set<string>();
    const uniqueStyleTemplates = new Set<string>();
    const uniqueOptions = new Set<string>();
    let totalRenderTimeRemaining = 0;
    
    // Iterate through filtered items to collect unique values
    this.filteredReleaseItems.forEach(item => {
      if (item.homeConfigId) uniqueHomeConfigs.add(item.homeConfigId);
      if (item.home) uniqueHomes.add(item.home);
      if (item.masterFile) uniqueMasterFiles.add(item.masterFile);
      if (item.styleTemplate) uniqueStyleTemplates.add(item.styleTemplate);
      
      // For options, we need to split them since they're stored as comma-separated values
      if (item.options) {
        item.options.split(',').forEach(option => {
          option = option.trim();
          if (option) uniqueOptions.add(option);
        });
      }

      if (item.releaseStatus !== ReleaseStatus.Live && item.releaseStatus !== ReleaseStatus.ApprovedForLive) {
        let renderTimeEstimate = 0;
        
        switch (item.productName) {
          case ProductName.Panorama:
            let panoEstimate = 0;
            if (!item?.latestVersion) panoEstimate += 2;
            if (!item?.higherResVersion) panoEstimate += 25;
            if (this.data?.situPlan && !item.situPlan) panoEstimate += 3;
            if (this.data?.situRender && !item.situRender) panoEstimate += 1;
            if (this.data?.situRaytracedPlus && !item.highestResVersion) panoEstimate += 466;
            renderTimeEstimate = panoEstimate;
            break;
          case ProductName.Facade:
            let facadeEstimate = 0;
            if (!item?.latestVersion) facadeEstimate += 5;
            if (!item?.higherResVersion) facadeEstimate += 10;
            renderTimeEstimate = facadeEstimate;
            break;
          default:
            renderTimeEstimate = 5;
        }
        
        totalRenderTimeRemaining += renderTimeEstimate;
      }
    });
    
    // Update the stats object
    this.releaseStats = {
      uniqueHomeConfigs: uniqueHomeConfigs.size,
      uniqueHomes: uniqueHomes.size,
      uniqueMasterFiles: uniqueMasterFiles.size,
      uniqueStyleTemplates: uniqueStyleTemplates.size,
      uniqueOptions: uniqueOptions.size,
      totalItems: this.filteredReleaseItems.length,
      totalRenderTimeRemaining: totalRenderTimeRemaining
    };
  }

  formatRenderTime(minutes: number): string {
    if (minutes < 60) {
      return `${minutes} min`;
    } else {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return remainingMinutes > 0 
        ? `${hours}h ${remainingMinutes}m` 
        : `${hours}h`;
    }
  }

  /* filtering */
  filterData() {
    this.pageLength = this.pageSize;
    this.filteredReleaseItems = this.filterReleaseItems(this.data?.releaseItems!, {
        updatedByName: this.searchTerm,
        comments: this.searchTerm,
        supportTask: this.searchTerm,
        home: this.selectedHome,
        masterFile: this.selectedMasterFile,
        options: this.selectedOption,
        styleTemplate: this.selectedStyleTemplate,
        releaseStatus: this.selectedReleaseStatus,
        productName: this.selectedProductName,
        hasComments: this.commentsFilter ? true : undefined,
        hasTickets: this.ticketFilter ? true : undefined,
        hasStandard: this.standardFilter ? true : undefined,
        hasHiRes: this.hiResFilter ? true : undefined,
        releaseIssue: this.issueFilter,
        isViewed: this.viewedFilter ? true : undefined,
    });
    
    // Update selectedReleaseItems to only include items in the filtered results
    const filteredItemIds = this.filteredReleaseItems
      .map(item => item.id)
      .filter((id): id is string => id !== undefined);
      
    this.selectedReleaseItems = this.selectedReleaseItems
      .filter(selectedId => filteredItemIds.includes(selectedId));
      
    this.sortData();
    this.calculateStatusPercentages();
    this.calculateReleaseStats();
    this.updateParams();
  }
  private filterReleaseItems(items: ReleaseItem[], filter: {
    updatedByName?: string,
    home?: string,
    masterFile?: string,
    options?: string,
    styleTemplate?: string,
    supportTask?: string,
    comments?: string,
    releaseStatus?: ReleaseStatus,
    productName?: ProductName,
    hasComments?: boolean,
    hasTickets?: boolean,
    hasStandard?: boolean,
    hasHiRes?: boolean,
    releaseIssue?: string,
    isViewed?: boolean
  }): ReleaseItem[] {
      return items.filter(item => {
          return  (
                    (filter.updatedByName === undefined ||
                      item.home?.includes(filter.updatedByName) || 
                      item.masterFile?.includes(filter.updatedByName) || 
                      item.options?.includes(filter.updatedByName) || 
                      item.styleTemplate?.includes(filter.updatedByName)
                    ) ||
                    (filter.comments === undefined || item.comments?.includes(filter.comments)) ||
                    (filter.supportTask === undefined || item.supportTask?.includes(filter.supportTask))
                  ) &&
                  (filter.home === undefined || item.home === filter.home) &&
                  (filter.masterFile === undefined || item.masterFile === filter.masterFile) &&
                  (filter.options === undefined || item.options?.includes(filter.options)) &&
                  (filter.styleTemplate === undefined || item.styleTemplate === filter.styleTemplate) &&
                  (filter.releaseStatus === undefined || item.releaseStatus === filter.releaseStatus) &&
                  (filter.productName === undefined || item.productName === filter.productName) &&
                  (filter.hasComments === undefined || item.comments) &&
                  (filter.hasTickets === undefined || item.supportTask) &&
                  (filter.hasStandard === undefined || item.latestVersion) &&
                  (filter.hasHiRes === undefined || item.higherResVersion) &&
                  (filter.isViewed === undefined || item.lastViewedBy) &&
                  (filter.releaseIssue === undefined || item.releaseIssueReleaseItems?.find(x => x.releaseIssueId == filter.releaseIssue));
      });
  }
  /* filtering */

  /* sorting */
  sortCriteria: string = 'home'; // or any default sorting field
  sortOrder: 'asc' | 'desc' = 'asc';
  sortData() {
    if (this.sortCriteria) {
      this.filteredReleaseItems.sort((a, b) => {
        let valueA = this.getFieldValue(a, this.sortCriteria);
        let valueB = this.getFieldValue(b, this.sortCriteria);
  
        if (valueA < valueB) {
          return this.sortOrder === 'asc' ? -1 : 1;
        } else if (valueA > valueB) {
          return this.sortOrder === 'asc' ? 1 : -1;
        } else {
          return 0;
        }
      });
    }
  }

  getEnumTextValue(enumType: any, value: number): string {
    return enumType[value];
  }

  getSortCriteriaName(field: string){
    switch (field) {
      case 'home':
        return 'Home';
      case 'masterFile':
        return 'MasterFile';
      case 'option':
        return 'Options';
      case 'styleTemplate':
        return 'Style Template';
      case 'releaseStatus':
        return 'Release Status';
      case 'renderType':
        return 'Render Type';
      default:
        return '';
    }
  }
  
  getFieldValue(item: ReleaseItem, field: string) {
    switch (field) {
      case 'home':
        return item.home ?? '';
      case 'masterFile':
        return item.masterFile ?? '';
      case 'option':
        return item.options ?? '';
      case 'styleTemplate':
        return item.styleTemplate ?? '';
      case 'releaseStatus':
        return item.releaseStatus !== undefined ? this.getEnumTextValue(ReleaseStatus, item.releaseStatus) : '';
      case 'renderType':
        return item.productName !== undefined ? this.getEnumTextValue(ProductName, item.productName) : '';
      default:
        return '';
    }
  }
  /* sorting */

  clearSearch() {
    this.searchTerm = undefined;
  }

  statusPercentages: { status: ReleaseStatus, percentage: number, count: number }[] = [];
  calculateStatusPercentages() {
    if (!this.filteredReleaseItems || this.filteredReleaseItems.length === 0) {
      return;
    }
  
    const totalItems = this.filteredReleaseItems.length;
    const statusCounts: { [key: string]: number } = {};
    const minDisplayPercentage = 5; // Minimum display percentage for any segment
  
    // Count the occurrences of each status
    this.filteredReleaseItems.forEach(item => {
      if (item.releaseStatus !== undefined) {
        const status = item.releaseStatus as unknown as string;
        statusCounts[status] = (statusCounts[status] || 0) + 1;
      }
    });
  
    // Calculate initial raw percentages
    let totalRawPercentage = 0;
    const rawPercentages = Object.keys(statusCounts).map(status => {
      const count = statusCounts[status] || 0;
      const percentage = (count / totalItems) * 100;
      totalRawPercentage += percentage;
      return {
        status: parseInt(status, 10) as ReleaseStatus,
        rawPercentage: percentage,
        count: count
      };
    });
  
    // Apply minimum display percentage and adjust the rest
    let totalRoundedPercentage = 0;
    let adjustedPercentages = rawPercentages.map((status, index, array) => {
      let percentage = Math.max(Math.round(status.rawPercentage), minDisplayPercentage);
      totalRoundedPercentage += percentage;
  
      // If it's the last element, adjust to ensure totalRoundedPercentage is 100%
      if (index === array.length - 1 && totalRoundedPercentage !== 100) {
        percentage += 100 - totalRoundedPercentage;
      }
  
      return {
        status: status.status,
        percentage: percentage,
        count: status.count
      };
    });
  
    // Check if the rounding caused the total to exceed 100%
    let totalAdjustedPercentage = adjustedPercentages.reduce((sum, status) => sum + status.percentage, 0);
  
    if (totalAdjustedPercentage !== 100) {
      const difference = 100 - totalAdjustedPercentage;
  
      // Adjust the percentages proportionally
      adjustedPercentages = adjustedPercentages.map((status, index) => {
        if (index === adjustedPercentages.length - 1) {
          status.percentage += difference; // Adjust the last element
        } else {
          const adjustment = Math.round((status.percentage / totalAdjustedPercentage) * difference);
          status.percentage += adjustment;
          totalAdjustedPercentage += adjustment;
        }
        return status;
      });
    }
  
    this.statusPercentages = adjustedPercentages;
  }
  
  private bulkSetReleaseItemProperties() {
    let request = new BulkUpdateReleaseItemsRequest();
    request.releaseItemIds = this.selectedReleaseItems;
    request.companyId = this.selectedCompany?.id;
    request.releaseItemFieldToUpdate = this.releaseItemFieldToUpdate;
    switch (this.releaseItemFieldToUpdate) {
      case ReleaseItemFieldToUpdate.Comments:
        request.comments = this.updateReleaseItemValue;
        break;
      case ReleaseItemFieldToUpdate.ReleaseStatus:
        request.releaseStatus = this.updateReleaseItemEnumValue;
        break;
      case ReleaseItemFieldToUpdate.SupportTicket:
        request.supportTicket = this.updateReleaseItemValue;
        break;
      case ReleaseItemFieldToUpdate.AssignToReleaseIssue:
        request.releaseIssueId = this.updateReleaseItemValue;
        break;
      case ReleaseItemFieldToUpdate.Delete:
        break;
    }

    this.situViewApiClient.bulkUpdateReleaseItems(request)
      .then((response: BulkUpdateReleaseItemsResponse) => {
        if (response.success) {
          this.load();
          this.clearValues();
          this.clearSelection();
        } else {
          this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
        }
      })
      .catch((err: any) => {
        this.snackBar.open(`Could not update the release items. Refresh Page.`, 'OK', { duration: 2000 });
      });
  }

  setReleaseItemViewed(releaseItemId: string) {
    let request = new SetReleaseItemViewedRequest();
    request.companyId = this.selectedCompany?.id;
    request.releaseId = this.id;
    request.releaseItemId = releaseItemId;

    this.situViewApiClient.setReleaseItemViewed(request)
      .then((response: SetReleaseItemViewedResponse) => {
        if (response?.success) {
          this.filteredReleaseItems = this.filteredReleaseItems.map(item => {
            if (item.id === releaseItemId) {
              item.lastViewedBy = this.authService.getUserId();
              item.lastViewedName = this.authService.getUserName();
              item.dateLastViewed = new Date();
            }
            return item;
          });
          if (this.data?.releaseItems) {
            this.data.releaseItems = this.data.releaseItems.map(item => {
              if (item.id === releaseItemId) {
                item.lastViewedBy = this.authService.getUserId();
                item.lastViewedName = this.authService.getUserName();
                item.dateLastViewed = new Date();
              }
              return item;
            });
          }
        }
      })
      .catch((err: any) => {})
      .finally(() => {});
  }

  showReleaseIssues: boolean = false;
  releaseIssuePriorities = ReleaseIssuePriority;
  releaseIssueStatuses = ReleaseIssueStatus;
  releaseIssues: ReleaseIssue[] = [];
  private loadReleaseIssues() {
    this.loading = true;
    let request = new ListReleaseIssuesRequest();
    //request.companyId = this.selectedCompany?.id;
    request.releaseId = this.id;
    request.includeStats = true;

    this.situViewApiClient.listReleaseIssues(request)
      .then((response: ListReleaseIssuesResponse) => {
        if (response.success && response.items) {
          this.releaseIssues = response.items;
        }
      })
      .catch((err: any) => {

      })
      .finally(() => {
        this.loading = false;
      });
  }

  releaseIssueDialogRef: MatDialogRef<ReleaseIssueDialogComponent> | undefined;
  addEditReleaseIssue(releaseIssueId?: string) {
    this.releaseIssueDialogRef = this.dialog.open(ReleaseIssueDialogComponent, {
      panelClass: 'modal-panel',
      width: '60%',
      height: '80%',
      data: {
        isSituUser: this.isSituUser,
        companyId: this.selectedCompany?.id,
        releaseIssueId: releaseIssueId,
        releaseId: this.id
      }
    });

    this._subscriptions.push(this.releaseIssueDialogRef.afterClosed()
      .subscribe((data: ReleaseIssueDialogComponentResultData) => {
        if (data?.refreshNeeded) {
          this.loadReleaseIssues();
        }
      })
    );
  }

  //selectedCard: string | undefined;
  onCardSelectClick(releaseIssueId: string | undefined) { 
    if (releaseIssueId !== this.issueFilter) {
      this.issueFilter = releaseIssueId;
    } else {
      this.issueFilter = undefined;
    }
    this.filterData();
  }

  private load(): void {
    this.loadReleaseIssues();
    if (this.released) {
      if (this.top100) {
        this.breadcrumbService.setParam('releaseName', "Top 100");
        let request = new GetMostPopularRenderedHomesByBuilderRequest();
        request.ownerId = this.selectedCompany?.id;
        request.pageNumber = 1;
        request.pageSize = 50;
        request.daysPreviousToSearch = 7;
    
        this.analyticsClient.getMostPopularRenderedHomesByBuilder(request)
          .then((response: GetMostPopularRenderedHomesByBuilderResponse) => {
            if (response.success) {
              this.viewControls.totalItems = response.totalItems!;
              let release = new Release();
              release.name = "Top 100";
              release.description = "Top 100 homes viewed in the past week";
              let releasedItems = new Array<ReleaseItem>();
              this.logItems = response.logItems;
              response.logItems?.forEach((renderedOutput: ViewMostPopularHomeLogItem) => {
                let releaseItem = new ReleaseItem();
                releaseItem.id = renderedOutput.artifactId;
                releaseItem.home = renderedOutput.homeName;
                releaseItem.releaseStatus = ReleaseStatus.Live;
                releaseItem.masterFile = renderedOutput.facade;
                releaseItem.options = renderedOutput.options;
                // releaseItem.homeConfig = renderedOutput.homeConfig;
                // releaseItem.homeConfigId = renderedOutput.homeConfigId;
                releaseItem.styleTemplate = renderedOutput.themes;
                releaseItem.latestVersion = renderedOutput.artifactId;
                releaseItem.productName = renderedOutput.productName;
                releasedItems.push(releaseItem);
              });
  
              release.releaseItems = releasedItems;
              this.data = release;
              this.filterData();
            } else {
              this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
            }
          })
          .catch((err: any) => {
            this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
          });
      } else {
        this.breadcrumbService.setParam('releaseName', "complete");
        let request = new ListRenderedOutputsRequest();
        request.companyId = this.selectedCompany?.id;
        request.isProduction = true;
        request.pageNumber = this.viewControls.pageIndex;
        request.pageSize = this.viewControls.pageSize;
        request.orderByHome = true;
        request.productName = ProductName.Panorama;
        request.expandProducts = true;
        if (this.searchTerm) request.containsWords = [this.searchTerm];
    
        this.situViewApiClient.listRenderedOutputs(request)
          .then((response: ListRenderedOutputsResponse) => {
            if (response.success) {
              this.viewControls.totalItems = response.totalItems!;
              let release = new Release();
              release.name = "Released";
              release.description = "Items that have already been released";
              let releasedItems = new Array<ReleaseItem>();
              response.renderedOutputs?.forEach((renderedOutput: RenderedOutput) => {
                let releaseItem = new ReleaseItem();
                releaseItem.id = renderedOutput.id;
                releaseItem.home = renderedOutput.homeConfig!.home;
                releaseItem.releaseStatus = ReleaseStatus.Live;
                releaseItem.masterFile = renderedOutput.homeConfig!.masterFile;
                releaseItem.options = renderedOutput.homeConfig!.options?.join(" ");
                releaseItem.homeConfig = renderedOutput.homeConfig;
                releaseItem.homeConfigId = renderedOutput.homeConfigId;
                releaseItem.styleTemplate = renderedOutput.styleConfigTemplate?.name;
                releaseItem.latestVersion = renderedOutput.id;
                releaseItem.productName = renderedOutput.productName;
                releasedItems.push(releaseItem);
              });
  
              release.releaseItems = releasedItems;
              this.data = release;
              this.filterData();
            } else {
              this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
            }
          })
          .catch((err: any) => {
            this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
          });
      }
    } else {
      let request = new GetReleaseRequest();
      request.releaseId = this.id;
      request.companyId = this.selectedCompany?.id;
  
      this.situViewApiClient.getRelease(request)
        .then((response: GetReleaseResponse) => {
          if (response.success) {
            if (response?.release?.companyId != this.selectedCompany?.id && response?.release?.companyId) {
              if (!this.authService.isAuthorizedForCompanyById(response.release.companyId)) {
                this.showMessage(`Sorry! Your are not authorized for company ${response.release.companyId}.`);
                this.router.navigateByUrl('/unauthorized');
                return;
              }
    
              this.companyService.getCompanyById(response.release.companyId)
                .then((company) => {
                  if (!company) return;
    
                  this.stateService.state = {
                    ...this.stateService.state,
                    company
                  };
                });
            }
            this.breadcrumbService.setParam('releaseName', response?.release?.name);
            this.data = response.release;
            this.filterData();
          } else {
            this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
          }
        })
        .catch((err: any) => {
          this.snackBar.open(`Could not fetch the release. Refresh Page.`, 'OK', { duration: 2000 });
        });
    }
  };

  private showMessage(message: string) {
    this.snackBar.open(message, 'Dismiss', {
      duration: 3000
    });
  }

  private updateParams() {
    const queryParams: { [key: string]: any } = {};

    if (this.searchTerm) {
      queryParams.search = this.searchTerm;
    }
    if (this.selectedHome) {
      queryParams.home = this.selectedHome;
    }
    if (this.selectedMasterFile) {
      queryParams.masterfile = this.selectedMasterFile;
    }
    if (this.selectedOption) {
      queryParams.options = this.selectedOption;
    }
    if (this.selectedStyleTemplate) {
      queryParams.template = this.selectedStyleTemplate;
    }
    if (this.selectedReleaseStatus !== undefined) {
      queryParams.status = this.selectedReleaseStatus;
    }
    if (this.selectedProductName !== undefined) {
      queryParams.type = this.selectedProductName;
    }
    if (this.commentsFilter) {
      queryParams[this.CommentsParamName] = this.commentsFilter.toString();
    }
    if (this.ticketFilter) {
      queryParams[this.TicketsParamName] = this.ticketFilter.toString();
    }
    if (this.standardFilter) {
      queryParams[this.StandardParamName] = this.standardFilter.toString();
    }
    if (this.hiResFilter) {
      queryParams[this.HiResParamName] = this.hiResFilter.toString();
    }
    if (this.viewedFilter) {
      queryParams[this.ViewedParamName] = this.viewedFilter.toString();
    }
    if (this.issueFilter) {
      queryParams.issue = this.issueFilter;
    }

    const urlTree = this.router.createUrlTree([], {
      queryParamsHandling: "merge",
      preserveFragment: true,
      queryParams: queryParams
    });

    if (!this.searchTerm) {
      delete urlTree.queryParams[this.SearchParamName];
    }
    if (!this.selectedHome) {
      delete urlTree.queryParams[this.HomeParamName];
    }
    if (!this.selectedMasterFile) {
      delete urlTree.queryParams[this.MasterFileParamName];
    }
    if (!this.selectedOption) {
      delete urlTree.queryParams[this.OptionsParamName];
    }
    if (!this.selectedStyleTemplate) {
      delete urlTree.queryParams[this.StyleTemplateParamName];
    }
    if (this.selectedReleaseStatus === undefined) {
      delete urlTree.queryParams[this.StatusParamName];
    }
    if (this.selectedProductName === undefined) {
      delete urlTree.queryParams[this.RenderTypeParamName];
    }
    if (!this.commentsFilter) {
      delete urlTree.queryParams[this.CommentsParamName];
    }
    if (!this.ticketFilter) {
      delete urlTree.queryParams[this.TicketsParamName];
    }
    if (!this.standardFilter) {
      delete urlTree.queryParams[this.StandardParamName];
    }
    if (!this.hiResFilter)  {
      delete urlTree.queryParams[this.HiResParamName];
    }    
    if (!this.viewedFilter)  {
      delete urlTree.queryParams[this.ViewedParamName];
    }
    if (!this.issueFilter)  {
      delete urlTree.queryParams[this.IssueParamName];
    }
  
    this.router.navigateByUrl(urlTree);
  }
}
